import React from 'react';

import { Stack } from '@mui/material';

import { isRecord } from '~/utils/isRecord';

import { ProposedValueField, type ProposedValuePath } from './ProposedValueField';
import { PREVIOUS_VALUE_KEY_ALIASES } from './valueDiff/constants';
import { humanizeKey } from './valueDiff/formatters';

interface ProposedValueEditorProps {
  value: Record<string, unknown>;
  previous: Record<string, unknown> | null;
  onChange: (value: Record<string, unknown>) => void;
}

export function updateProposedValueAtPath(
  value: Record<string, unknown>,
  path: ProposedValuePath,
  updatedValue: unknown,
): Record<string, unknown> {
  const update = (current: unknown, depth: number): unknown => {
    if (depth === path.length) {
      return updatedValue;
    }

    const segment = path[depth];
    if (Array.isArray(current) && typeof segment === 'number') {
      const next = current.slice();
      next[segment] = update(current[segment], depth + 1);
      return next;
    }
    if (isRecord(current) && typeof segment === 'string') {
      return { ...current, [segment]: update(current[segment], depth + 1) };
    }
    return current;
  };

  return update(value, 0) as Record<string, unknown>;
}

export default function ProposedValueEditor({ value, previous, onChange }: ProposedValueEditorProps) {
  const handleFieldChange = (path: ProposedValuePath, updatedValue: unknown) => {
    onChange(updateProposedValueAtPath(value, path, updatedValue));
  };

  return (
    <Stack spacing={1.5}>
      {Object.entries(value).map(([key, fieldValue]) => {
        const previousKey = previous && key in previous ? key : PREVIOUS_VALUE_KEY_ALIASES[key];
        return (
          <ProposedValueField
            key={key}
            label={humanizeKey(key)}
            value={fieldValue}
            previous={previousKey ? previous?.[previousKey] : undefined}
            path={[key]}
            onChange={handleFieldChange}
          />
        );
      })}
    </Stack>
  );
}
